import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import type { ColumnAlign } from "../Table/Table";

export interface DataTableColumn<Row> {
  readonly id: string;
  readonly header: ReactNode;
  readonly cell: (row: Row) => ReactNode;
  readonly align?: ColumnAlign;
  readonly numeric?: boolean;
  /** Makes the header a sort button; returns a comparable value for the row. */
  readonly sortValue?: (row: Row) => string | number;
}

export interface DataTableProps<Row> {
  readonly columns: ReadonlyArray<DataTableColumn<Row>>;
  readonly rows: ReadonlyArray<Row>;
  readonly getRowId: (row: Row) => string;
  /** Accessible table name. */
  readonly caption: string;
  /** Whole row opens the item (click, Enter or Space). Inner buttons/links keep their own action. */
  readonly onRowClick?: (row: Row) => void;
  /** Accessible name of the row's open action. */
  readonly rowLabel?: (row: Row) => string;
  /** Trailing actions cell (e.g. a menu); clicks inside never trigger `onRowClick`. */
  readonly rowActions?: (row: Row) => ReactNode;
  readonly actionsHeader?: ReactNode;
  /** Extra attributes (e.g. `data-testid`, `data-status`) set on each body row. */
  readonly rowAttrs?: (row: Row) => Readonly<Record<`data-${string}`, string>>;
  readonly emptyMessage?: ReactNode;
  /** Rows per page; omit for no pagination. */
  readonly pageSize?: number;
  readonly className?: string;
  readonly testId?: string;
}

const ALIGN: Readonly<Record<ColumnAlign, string>> = {
  start: "text-start",
  center: "text-center",
  end: "text-end",
};

type Sort = { readonly id: string; readonly dir: "asc" | "desc" } | null;

const INTERACTIVE =
  "a,button,input,select,textarea,label,[role=menu],[role=menuitem],summary,details";

/**
 * Shared list table: sticky header, row dividers + hover, sortable columns, clickable rows,
 * per-row actions, empty state, row count and pagination. Logical CSS only (RTL-first).
 */
export function DataTable<Row>({
  columns,
  rows,
  getRowId,
  caption,
  onRowClick,
  rowLabel,
  rowActions,
  actionsHeader,
  rowAttrs,
  emptyMessage,
  pageSize,
  className,
  testId,
}: DataTableProps<Row>) {
  const { t, locale } = useI18n();
  const [sort, setSort] = useState<Sort>(null);
  const [page, setPage] = useState(0);
  const num = (n: number) => n.toLocaleString(locale);

  const sorted = useMemo(() => {
    const col = sort ? columns.find((c) => c.id === sort.id) : undefined;
    if (!sort || !col?.sortValue) return rows;
    const sv = col.sortValue;
    const sign = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const x = sv(a);
      const y = sv(b);
      return (x < y ? -1 : x > y ? 1 : 0) * sign;
    });
  }, [rows, columns, sort]);

  const pages = pageSize ? Math.max(1, Math.ceil(sorted.length / pageSize)) : 1;
  const current = Math.min(page, pages - 1);
  const from = pageSize ? current * pageSize : 0;
  const visible = pageSize ? sorted.slice(from, from + pageSize) : sorted;
  const alignOf = (c: DataTableColumn<Row>) => ALIGN[c.align ?? (c.numeric ? "end" : "start")];
  const colCount = columns.length + (rowActions ? 1 : 0);

  const toggleSort = (id: string) => {
    setPage(0);
    setSort((s) =>
      s?.id !== id ? { id, dir: "asc" } : s.dir === "asc" ? { id, dir: "desc" } : null,
    );
  };

  const onKey = (e: KeyboardEvent<HTMLTableRowElement>, row: Row) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onRowClick?.(row);
    }
  };

  return (
    <div className={cx("rounded-lg border border-line bg-surface", className)} data-testid={testId}>
      <div className="max-h-[70vh] overflow-auto max-sm:max-h-none max-sm:overflow-visible">
        <table className="w-full border-collapse text-sm max-sm:block">
          <caption className="sr-only">{caption}</caption>
          <thead className="sticky top-0 z-10 bg-surface-muted max-sm:sr-only">
            <tr>
              {columns.map((c) => {
                const active = sort?.id === c.id ? sort : null;
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={
                      active ? (active.dir === "asc" ? "ascending" : "descending") : undefined
                    }
                    className={cx(
                      "px-4 py-3 font-semibold whitespace-nowrap text-fg-muted",
                      alignOf(c),
                    )}
                  >
                    {c.sortValue ? (
                      <button
                        type="button"
                        className="cursor-pointer font-semibold focus-visible:outline-2 focus-visible:outline-focus"
                        onClick={() => toggleSort(c.id)}
                      >
                        {c.header}
                        <span aria-hidden="true">
                          {active ? (active.dir === "asc" ? " ▲" : " ▼") : ""}
                        </span>
                      </button>
                    ) : (
                      c.header
                    )}
                  </th>
                );
              })}
              {rowActions ? (
                <th scope="col" className="px-4 py-3 text-end font-semibold text-fg-muted">
                  {actionsHeader ?? <span className="sr-only">{t("ui.table.actions")}</span>}
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody className="max-sm:block max-sm:space-y-3 max-sm:p-3">
            {visible.length === 0 ? (
              <tr className="max-sm:block">
                <td colSpan={colCount} className="px-4 py-8 max-sm:block text-center text-fg-muted">
                  {emptyMessage ?? t("ui.table.empty")}
                </td>
              </tr>
            ) : (
              visible.map((row) => (
                <tr
                  key={getRowId(row)}
                  data-row-id={getRowId(row)}
                  {...rowAttrs?.(row)}
                  className={cx(
                    "border-t border-line hover:bg-surface-muted",
                    // HUNT-06: below sm each row becomes a stacked card (no clipped columns).
                    "max-sm:block max-sm:rounded-xl max-sm:border max-sm:border-line max-sm:p-3",
                    onRowClick && "cursor-pointer",
                  )}
                  {...(onRowClick
                    ? {
                        tabIndex: 0,
                        "aria-label": rowLabel?.(row),
                        onClick: (e: React.MouseEvent) => {
                          if ((e.target as HTMLElement).closest(INTERACTIVE)) return;
                          onRowClick(row);
                        },
                        onKeyDown: (e: KeyboardEvent<HTMLTableRowElement>) => onKey(e, row),
                      }
                    : {})}
                >
                  {columns.map((c) => (
                    <td
                      key={c.id}
                      data-label={typeof c.header === "string" ? c.header : undefined}
                      className={cx(
                        "px-4 py-3 text-fg",
                        "max-sm:flex max-sm:items-baseline max-sm:justify-between max-sm:gap-3 max-sm:px-0 max-sm:py-1.5 max-sm:text-start max-sm:before:text-xs max-sm:before:text-fg-muted max-sm:before:content-[attr(data-label)]",
                        alignOf(c),
                        c.numeric && "tabular-nums",
                      )}
                    >
                      {c.numeric ? <bdi dir="ltr">{c.cell(row)}</bdi> : c.cell(row)}
                    </td>
                  ))}
                  {rowActions ? (
                    <td
                      className="px-4 py-3 text-end max-sm:block max-sm:px-0 max-sm:py-1.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {rowActions(row)}
                    </td>
                  ) : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {rows.length > 0 ? (
        <div className="flex items-center justify-between gap-3 border-t border-line px-4 py-2 text-xs text-fg-muted">
          <span role="status">
            {pageSize
              ? t("ui.table.range", {
                  from: num(from + 1),
                  to: num(Math.min(from + pageSize, sorted.length)),
                  total: num(sorted.length),
                })
              : t("ui.table.count", { count: rows.length })}
          </span>
          {pageSize && pages > 1 ? (
            <span className="flex gap-2">
              <button type="button" disabled={current === 0} onClick={() => setPage(current - 1)}>
                {t("ui.table.prev")}
              </button>
              <button
                type="button"
                disabled={current >= pages - 1}
                onClick={() => setPage(current + 1)}
              >
                {t("ui.table.next")}
              </button>
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
